import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import type { CampaignDetailHeader } from '#/entities/campaign'
import { formatNumber } from '#/shared/lib/format'
import { Button } from '#/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { ChevronDownIcon, PowerDialIcon } from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'
import { Tooltip } from '#/shared/ui/tooltip'

import { useCampaignDialSlices } from '../model/use-campaign-dial-slices'
import type { CampaignDialSlice } from '../model/use-campaign-dial-slices'
import { useCampaignPowerDial } from '../model/use-campaign-power-dial'
import type { CampaignContactFilter } from '../model/contact-filters'

/** One slice: its name, and how many people are in it. A slice with nobody
 *  in it stays listed — so the menu reads the same every time — but can't
 *  be picked. */
function DialSliceItem({
  slice,
  onPick,
}: {
  slice: CampaignDialSlice
  onPick: (filter: CampaignContactFilter) => void
}) {
  return (
    <DropdownMenuItem
      className="h-8.5 justify-between gap-6"
      disabled={slice.count === 0}
      onSelect={() => onPick(slice.key)}
    >
      <span className="truncate">{slice.label}</span>
      {slice.count == null ? (
        <Skeleton className="h-3 w-6" />
      ) : (
        <span className="text-xs text-gray-500 tabular-nums">
          {formatNumber(slice.count)}
        </span>
      )}
    </DropdownMenuItem>
  )
}

/** The chevron half: which slice of the audience to dial. */
function DialSliceMenu({
  campaignId,
  disabled,
  onPick,
}: {
  campaignId: string
  disabled: boolean
  onPick: (filter: CampaignContactFilter) => void
}) {
  const { t } = useTranslation()
  const slices = useCampaignDialSlices(campaignId)
  return (
    <DropdownMenu onOpenChange={slices.onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          className="-ml-px rounded-l-none px-2"
          disabled={disabled}
          aria-label={t('campaigns.powerDial.chooseAria')}
        >
          <ChevronDownIcon className="size-4 text-gray-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-medium text-gray-500">
          {t('campaigns.powerDial.menuLabel')}
        </DropdownMenuLabel>
        {slices.groups.map((group, index) => (
          <Fragment key={group[0]?.key ?? index}>
            {index > 0 && <DropdownMenuSeparator />}
            {group.map((slice) => (
              <DialSliceItem key={slice.key} slice={slice} onPick={onPick} />
            ))}
          </Fragment>
        ))}
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-xs leading-relaxed text-gray-500">
          {t('campaigns.powerDial.menuNote')}
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Queues the campaign's people into the dialer — a split button. The main
 * half dials everyone; its badge is the service's `power_dial_count`, what
 * that click will actually queue, already capped at 500. The chevron picks a
 * narrower slice (the Contact tab's chips). Nothing to dial is a real state
 * (nobody with both a mobile and a quote), and saying so on a dead button
 * beats a click that reports it as an error — every slice is a subset of
 * everyone, so the menu goes dead with it.
 */
export function CampaignPowerDialButton({
  header,
}: {
  header: CampaignDetailHeader
}) {
  const { t } = useTranslation()
  const powerDial = useCampaignPowerDial(header.id)
  const empty = header.powerDialCount === 0
  const disabled = empty || powerDial.isStarting
  const group = (
    <div
      role="group"
      aria-label={t('campaigns.detail.actions.startPowerDialing')}
      className="inline-flex"
    >
      <Button
        variant="outline"
        size="xs"
        className="gap-1.5 rounded-r-none px-3.5"
        disabled={disabled}
        loading={powerDial.isStarting}
        onClick={() => powerDial.start()}
      >
        <PowerDialIcon className="size-4.5 text-primary" />
        {t('campaigns.detail.actions.startPowerDialing')}
        {!empty && (
          <span className="text-xs text-gray-300 tabular-nums">
            {formatNumber(header.powerDialCount)}
          </span>
        )}
      </Button>
      <DialSliceMenu
        campaignId={header.id}
        disabled={disabled}
        onPick={powerDial.start}
      />
    </div>
  )
  if (!empty) return group
  return (
    <Tooltip content={t('campaigns.powerDial.nobody')}>
      <span className="inline-flex">{group}</span>
    </Tooltip>
  )
}
